import clsx from 'clsx'
import { Link } from 'react-router-dom'
import { Lock } from '@/assets/icons'

export type SideNavItemProps = {
  /** The label text displayed in the nav item */
  label: string
  /** The route to navigate to */
  to: string
  /** Icon component to display (from @/assets/icons) */
  icon?: React.FC<React.SVGProps<SVGSVGElement>>
  /** Whether the item is currently active/selected */
  isActive?: boolean
  /** Whether to show the lock icon (for unauthenticated users) */
  showLock?: boolean
  /** Whether to show the beta tag */
  showBetaTag?: boolean
  /** Click handler for the nav item */
  onClick?: () => void
  /** Whether the item is disabled */
  disabled?: boolean
  /** Additional classes to add to the icon element - Used for home icon padding */
  iconClassName?: string
}

/**
 * A side navigation item component.
 *
 * ## States
 * - **Default**: Neutral text/icon colors
 * - **Hover**: Light background highlight (automatic via Tailwind)
 * - **Focus**: Blue outline ring (automatic via Tailwind)
 * - **Active**: Blue text/icon with light background (via `isActive` prop)
 * - **Disabled**: Reduced opacity, no interactions (via `disabled` prop)
 *
 * For collapsible groups with sub-items, use SideNavGroup instead.
 */
export function SideNavItem({
  label,
  to,
  icon: Icon,
  isActive = false,
  showLock = false,
  showBetaTag = false,
  onClick,
  disabled = false,
  iconClassName,
}: SideNavItemProps) {
  return (
    <Link
      to={to}
      data-ds-component="SideNavItem"
      onClick={disabled ? (e) => e.preventDefault() : onClick}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : undefined}
      className={clsx(
        // Base styles
        'group flex w-full items-center gap-2 rounded-md p-2 text-sm font-semibold leading-5 h-10',
        // Focus styles (keyboard navigation - not subitems)
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
        // State styles
        isActive ? 'bg-brand-neutral-100 text-brand-500' : 'text-brand-neutral-950 hover:bg-brand-neutral-100 active:bg-brand-neutral-200',
        // Disabled styles
        disabled && 'pointer-events-none opacity-50'
      )}
      aria-current={isActive ? 'page' : undefined}
    >
      {Icon && (
        <Icon
          className={clsx('w-5 h-5 shrink-0', isActive ? 'text-brand-500' : 'text-brand-neutral-600 group-hover:text-brand-neutral-700', iconClassName)}
          aria-hidden="true"
        />
      )}

      <span className={clsx('font-rethink-sans', isActive && 'text-brand-500')}>{label}</span>

      {showLock && <Lock className="ml-auto h-5 w-5 shrink-0 text-brand-neutral-500" aria-hidden="true" />}

      {showBetaTag && !showLock && (
        <span className="ml-auto shrink-0 rounded bg-brand-neutral-950 px-1 py-px text-[10px] font-medium leading-[11px] text-white [font-variant:small-caps]">
          beta
        </span>
      )}
    </Link>
  )
}
